import { LOADING, Loading } from "common/components/types";
import { logError } from "common/debug-print";
import { getLanguagePart, shouldUseImperialUnits } from "common/locale";
import { useLanguage } from "hooks/use-language";
import * as React from "react";
import { callCloudFunctionWeb } from "sharedUI/lib/function-utils";

// These were obtained through examining the output of a few API calls.
// There may be a type definition but 12 seconds of DuckDuckGo provided no conclusive evidence.

// Important fields:
// OpenWeatherMapData.main.temp -> Temperature (Kelvin)
// OpenWeatherMapData.weather[n].description -> Cloud conditions
// OpenWeatherMapData.name -> City
// OpenWeatherMapData.timezone -> Seconds difference from UTC.

export type OpenWeatherMapData = {
    coord: {
        lat: number;
        lon: number;
    };
    weather: {
        id: number;
        main: string;
        description: string;
        icon: string;
    }[];
    base: string;
    main: {
        temp: number;
        feels_like: number;
        temp_min: number;
        temp_max: number;
        pressure: number;
        humidity: number;
    };
    visibility: number;
    wind: {
        speed: number;
        deg: number;
        gust: number;
    };
    clouds: {
        all: number;
    };
    dt: number;
    sys: {
        type: number;
        id: number;
        country: string;
        sunrise: number;
        sunset: number;
    };
    timezone: number;
    id: number;
    name: string;
    cod: number;
};

export type TemperatureUnits = "standard" | "metric" | "imperial";
export type WeatherData = undefined | Loading | OpenWeatherMapData;

export type UseWeather = {
    units: TemperatureUnits;
    weather: WeatherData;
};

export const UNITS_TEMPERATURE_TEXT: Record<TemperatureUnits, string> = {
    standard: "K",
    metric: "°C",
    imperial: "°F",
};

export function unitsTemperatureText(units: TemperatureUnits) {
    return UNITS_TEMPERATURE_TEXT[units];
}

export function useWeather(latitude: number | undefined, longitude: number | undefined): UseWeather {
    const [loading, setLoading] = React.useState<boolean>(true);
    const [weather, setWeather] = React.useState<OpenWeatherMapData | undefined>(undefined);
    const language = useLanguage();

    const lang = React.useMemo(() => {
        return getLanguagePart(language);
    }, [language]);

    const units = React.useMemo(() => (shouldUseImperialUnits(language) ? "imperial" : "metric"), [language]);

    React.useEffect(() => {
        if (latitude === undefined || longitude === undefined) return;

        setLoading(true);
        const getWeather = async () => {
            try {
                const body = { latitude, longitude, units, lang: getLanguagePart(lang) };
                const result = await callCloudFunctionWeb("callOpenWeatherMapApi", body);
                const data = await result?.json();
                if (data === undefined) {
                    setWeather(undefined);
                    return;
                }
                setWeather(data as OpenWeatherMapData);
            } catch (err: unknown) {
                logError("Failed to fetch weather data", err);
            } finally {
                setLoading(false);
            }
        };
        void getWeather();
    }, [lang, latitude, longitude, units]);

    return {
        units,
        weather: loading ? LOADING : weather,
    };
}
